import React from "react";
import { Users, Package, ShoppingCart, UserCheck } from "lucide-react";
import DashboardCard from "./components/DashboardCard";
import { useUser } from "../../../hooks/useUser";
import { useOrder } from "../../../hooks/useOrder";
import { useFetchProducts } from "../../../hooks/useFetchProduct";
import { useFetchProductOwners } from "../../../hooks/useFetchProduct";
import LoadingComponent from "../../../components/form-components/LoadingComponent";

const Dashboard: React.FC = () => {
  const { getUsers, loading: userLoading } = useUser();
  const { orders, loading: orderLoading } = useOrder();
  const { products, loading: productLoading } = useFetchProducts();
  const { productOwners, loading: ownerLoading } = useFetchProductOwners();

  const [userStats, setUserStats] = React.useState({
    total: 0,
    verified: 0,
    unverified: 0,
  });
  const [orderStats, setOrderStats] = React.useState({
    total: 0,
    completed: 0,
    pending: 0,
  });
  const [productStats, setProductStats] = React.useState({
    total: 0,
    verified: 0,
    unverified: 0,
  });
  const [ownerStats, setOwnerStats] = React.useState({
    total: 0,
    verified: 0,
    unverified: 0,
  });

  React.useEffect(() => {
    const fetchUsers = async () => {
      const users = await getUsers();
      const verified = users.filter((u) => u.isVerified).length;
      setUserStats({
        total: users.length,
        verified,
        unverified: users.length - verified,
      });
    };
    fetchUsers();
  }, [getUsers]);

  React.useEffect(() => {
    const completed = orders.filter((o) => o.status === "completed").length;
    setOrderStats({
      total: orders.length,
      completed,
      pending: orders.length - completed,
    });
  }, [orders]);

  React.useEffect(() => {
    const verified = products.filter((p) => p.isVerified).length;
    setProductStats({
      total: products.length,
      verified,
      unverified: products.length - verified,
    });
  }, [products]);

  React.useEffect(() => {
    const verified = productOwners.filter((p) => p.isVerified).length;
    setOwnerStats({
      total: productOwners.length,
      verified,
      unverified: productOwners.length - verified,
    });
  }, [productOwners]);

  if (userLoading || orderLoading || productLoading || ownerLoading) {
    return <LoadingComponent />;
  }

  const dashboardCards = [
    {
      title: "Total Users",
      icon: Users,
      color: "bg-blue-100",
      stats: userStats,
      path: "/users",
    },
    {
      title: "Total Orders",
      icon: Package,
      color: "bg-yellow-100",
      stats: {
        total: orderStats.total,
        verified: orderStats.completed,
        unverified: orderStats.pending,
      },
      path: "/orders",
    },
    {
      title: "Total Products",
      icon: ShoppingCart,
      color: "bg-green-100",
      stats: productStats,
      path: "/products",
    },
    {
      title: "Total Shops",
      icon: UserCheck,
      color: "bg-red-100",
      stats: ownerStats,
      path: "/product-owners",
    },
  ];

  return (
    <>
      <h1 className="text-xl md:text-2xl font-bold mb-6 animate-slideUp">
        Dashboard
      </h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardCards.map((stat, index) => (
          <div
            key={index}
            className="animate-slideUp"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <DashboardCard {...stat} />
          </div>
        ))}
      </div>
    </>
  );
};

export default Dashboard;
